import SendIcon from '@mui/icons-material/Send';
import { Alert, Button, Stack } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { DateTime } from 'luxon';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import { useGetAllFiles } from '../../../sic-lib/api/hooks/useMyFile';
import { addMediaToContactComposeMessage } from '../../../store/conversation';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { cleanMediaDrawer } from '../../../store/media';
import { DrawerCommon } from '../../Shared/Drawer/DrawerCommon';
import Loader from '../../Shared/Loader';
import PillTabContent from '../../Shared/PillTabContent';
import { PillTab, PillTabs } from '../../Shared/PillTabs';
import SicMediaTab from './SicMediaTab';
import UploadMediaTab from './UploadMediaTab';

type TabOptions = 'myFiles' | 'User';

interface Props {
    contactId: number;
}

export default function MediaDrawer({ contactId }: Props) {
    const { classes } = useStyles();
    const dispatch = useAppDispatch();

    const [selectedTab, setTabValue] = React.useState<TabOptions>('myFiles');

    const mediaDrawerOpen = useAppSelector(state => state.media.mediaDrawer);
    const selected = useAppSelector(state => state.media.selectedMedia);
    const maxAttachmentReached = useAppSelector(state => state.media.maxAttachmentReached);

    const { data: filesData, isLoading: isFileLoading } = useGetAllFiles();

    const myFiles = React.useMemo(
        () =>
            filesData?.sort(
                (a, b) =>
                    DateTime.fromISO(b.dateModified).toMillis() -
                    DateTime.fromISO(a.dateModified).toMillis(),
            ) || [],
        [filesData],
    );

    const handleClose = () => {
        dispatch(cleanMediaDrawer());
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
        setTabValue(newValue as TabOptions);
    };

    const handleSendFiles = async () => {
        dispatch(
            addMediaToContactComposeMessage({
                contactId,
                media: selected.map(file => file.url) || undefined,
            }),
        );
        handleClose();
    };

    return (
        <DrawerCommon
            open={mediaDrawerOpen}
            title="Attach Files"
            handleOnClose={handleClose}
            width="1200px"
            footer={
                <Stack p={2} spacing={2} alignItems="center">
                    {maxAttachmentReached && (
                        <Alert severity="info" variant="outlined">
                            You have reached the maximum of 10 attachments.
                        </Alert>
                    )}
                    <PillTabs value={selectedTab} onChange={handleTabChange}>
                        <PillTab value="myFiles" label="My Files" />
                        <PillTab value="User" label="Upload Files" />
                    </PillTabs>
                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        endIcon={<SendIcon />}
                        disabled={selected?.length === 0}
                        onClick={handleSendFiles}
                    >
                        Attach
                        {selected && selected?.length > 0 && (
                            <>
                                {' '}
                                {selected?.length}
                                {selected?.length > 1 ? ' Files' : ' File'}
                            </>
                        )}
                    </Button>
                </Stack>
            }
        >
            <Stack spacing={2} p={2} height="100%">
                {/* {isErrorSummary && <AxiosErrorAlert error={errorSummary} className={classes.error} />} */}
                <Loader isLoading={isFileLoading}>
                    <>
                        <PillTabContent tabValue="myFiles" selectedTab={selectedTab}>
                            <SicMediaTab files={myFiles} />
                        </PillTabContent>
                        <PillTabContent tabValue="User" selectedTab={selectedTab}>
                            <UploadMediaTab contactId={contactId} onCloseHandler={handleClose} />
                        </PillTabContent>
                    </>
                </Loader>
            </Stack>
        </DrawerCommon>
    );
}

const useStyles = makeStyles()((theme: Theme) => ({
    paper: {
        maxWidth: '800px',
        width: '100%',
    },
    contentActions: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-end',
        flex: 1,
    },
    root: {
        height: '100%',
        overflowY: 'scroll',
        overflowX: 'hidden',
        padding: theme.spacing(0, 2),
    },
    loader: {},
    tabWrapper: {
        display: 'flex',
        borderRadius: '20px',
        backgroundColor: theme.palette.tabPills.main,
    },
    audio: {
        display: 'flex',
        flex: 1,
    },
    error: {
        margin: theme.spacing(1),
    },
}));
